import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DriverDetail {
  id: string;
  name: string;
  cpf?: string;
  cnh?: string;
  cnh_category?: string;
  cnh_valid_until?: string;
  phone?: string;
  email?: string;
  status: string;
  created_at: string;
  company_id: string;
}

export interface DriverTrip {
  id: string;
  driver_id: string;
  vehicle_id: string;
  origin: string;
  destination: string;
  start_at: string;
  end_at?: string;
  status: string;
  distance_actual_km?: number;
  fleet_vehicles?: {
    plate: string;
  };
}

export interface DriverAlert {
  id: string;
  driver_id: string;
  alert_type: string;
  priority: string;
  status: string;
  description: string;
  created_at: string;
}

export interface DriverDocument {
  id: string;
  driver_id: string;
  document_type: string;
  name: string;
  document_url?: string;
  expires_at?: string;
  status: string;
  created_at: string;
}

export interface DriverAssignment {
  id: string;
  driver_id: string;
  vehicle_id: string;
  status: string;
  created_at: string;
  fleet_vehicles: {
    plate: string;
    make?: string;
    model?: string;
  };
}

// Hook principal
export function useDriverDetails(driverId?: string) {
  return useQuery({
    queryKey: ['driver-detail', driverId],
    queryFn: async () => {
      if (!driverId) throw new Error('Driver ID required');
      
      const { data, error } = await supabase
        .from('fleet_drivers')
        .select('*')
        .eq('id', driverId)
        .single();

      if (error) throw error;
      return data as DriverDetail;
    },
    enabled: !!driverId
  });
}

// Hook para viagens do motorista
export function useDriverTrips(driverId?: string, companyId?: string) {
  return useQuery({
    queryKey: ['driver-trips', driverId, companyId],
    queryFn: async () => {
      if (!driverId || !companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_trips')
        .select(`
          id,
          driver_id,
          vehicle_id,
          origin,
          destination,
          start_at,
          end_at,
          status,
          distance_actual_km,
          fleet_vehicles (
            plate
          )
        `)
        .eq('driver_id', driverId)
        .eq('company_id', companyId)
        .order('start_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data || []) as DriverTrip[];
    },
    enabled: !!driverId && !!companyId
  });
}

// Hook para alertas do motorista
export function useDriverAlerts(driverId?: string, companyId?: string) {
  return useQuery({
    queryKey: ['driver-alerts', driverId, companyId],
    queryFn: async () => {
      if (!driverId || !companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_alerts')
        .select('*')
        .eq('driver_id', driverId)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as DriverAlert[];
    },
    enabled: !!driverId && !!companyId
  });
}

// Hook para documentos do motorista
export function useDriverDocuments(driverId?: string, companyId?: string) {
  return useQuery({
    queryKey: ['driver-documents', driverId, companyId],
    queryFn: async () => {
      if (!driverId || !companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_documents')
        .select('*')
        .eq('driver_id', driverId)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as DriverDocument[];
    },
    enabled: !!driverId && !!companyId
  });
}

// Hook para atribuições do motorista
export function useDriverAssignments(driverId?: string, companyId?: string) {
  return useQuery({
    queryKey: ['driver-assignments', driverId, companyId],
    queryFn: async () => {
      if (!driverId || !companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_assignments')
        .select(`
          id,
          driver_id,
          vehicle_id,
          status,
          created_at,
          fleet_vehicles (
            plate,
            make,
            model
          )
        `)
        .eq('driver_id', driverId)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as DriverAssignment[];
    },
    enabled: !!driverId && !!companyId
  });
}

// Hook para acesso mobile do motorista
export function useDriverMobileAccess(driverId?: string) {
  return useQuery({
    queryKey: ['driver-mobile-access', driverId],
    queryFn: async () => {
      if (!driverId) return null;
      
      const { data, error } = await supabase
        .from('fleet_driver_auth')
        .select('*')
        .eq('driver_id', driverId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!driverId
  });
}
